import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Typography, Grid, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate, LOCALE_MAP } from 'utils/locale-formatting';
import { PolicyCard } from '../../Shared';
import { getProtectedPolicy } from 'services/transparency-service';

/**
 * PoliciesTab - Policy list with detail modal
 * @param {Array} policies - Policy list
 * @param {string} companyKey - Company slug
 * @param {string} accessToken - Optional access token
 */
export const PoliciesTab = ({ policies = [], companyKey, accessToken }) => {
  const { t } = useTranslation('transparency');
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  if (!policies || policies.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('protected.emptyPolicies')}
      </Typography>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {policies.map((policy, index) => (
          <Grid item xs={12} sm={6} key={policy.id || index}>
            <PolicyCard
              name={policy.title}
              version={policy.version}
              status={policy.status}
              visibility={policy.visibility}
              lastUpdated={policy.lastUpdated}
              onClick={() => setSelectedPolicy(policy)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Policy Detail Modal */}
      {selectedPolicy && (
        <PolicyDetailModal
          policy={selectedPolicy}
          companyKey={companyKey}
          accessToken={accessToken}
          onClose={() => setSelectedPolicy(null)}
        />
      )}
    </Box>
  );
};

const PolicyDetailModal = ({ policy, companyKey, accessToken, onClose }) => {
  const { t, i18n } = useTranslation('transparency');
  const locale = LOCALE_MAP[i18n.language] ?? 'en-US';
  const { data: policyData, isLoading } = useQuery(
    ['policyDetail', policy.id, companyKey],
    () => getProtectedPolicy(policy.id, companyKey, accessToken),
    { enabled: !!policy.id }
  );

  const detail = policyData?.data;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {policy.title}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : detail ? (
          <Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('protected.policyDialog.generalInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('protected.policyDialog.version')}
                  </Typography>
                  <Typography variant="body1">{detail.version || policy.version}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('protected.policyDialog.status')}
                  </Typography>
                  <Typography variant="body1">{detail.status || policy.status}</Typography>
                </Grid>
                {detail.lastUpdated && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('protected.policyDialog.lastUpdated')}
                    </Typography>
                    <Typography variant="body1">{formatDate(detail.lastUpdated, locale)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {detail.executiveSummary && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('protected.policyDialog.executiveSummary')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detail.executiveSummary}
                  </Typography>
                </Paper>
              </Box>
            )}

            {detail.content && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('protected.policyDialog.policyContent')}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detail.content}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('protected.policyDetailUnavailable')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('protected.policyDialog.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
